import { LeadForm } from './LeadForm'
import { useLocale } from '@/i18n/locale'
import './final-cta.css'

const COPY = {
  ru: {
    eyebrow: 'Бесплатная ИИ-диагностика бизнеса',
    heading: 'Разберём ваш бизнес с ИИ',
    lede: 'Антон с ИИ в реальном времени смотрит, где в операционке узкие места и какие из них ИИ закроет первыми. Уйдёте с картой ИИ-зрелости бизнеса и планом развития — вне зависимости от того, будем работать или нет.',
    flowKicker: 'Как устроена бесплатная диагностика',
    steps: [
      { label: 'Заявка', time: '≈ 1 минута', body: 'Заполняете форму. В течение часа отвечаем туда, куда вы указали, — с подтверждением и вопросами.' },
      { label: 'Бриф', time: '5 минут', body: '3–5 вопросов про бизнес, узкие места и ориентир по бюджету, чтобы на созвоне работать по сути.' },
      { label: 'Созвон с ИИ', time: '30–90 минут', body: 'Антон и ИИ в реальном времени разбирают ваш бизнес: вы видите, как ИИ находит рычаги в операционке.' },
      { label: 'Карта ИИ-зрелости', time: '24–48 часов', body: 'PDF на 5–7 страниц, который читается как ТЗ для команды.' },
    ],
    mapLabel: 'В карте',
    map: 'схема операционки · 3–5 точек высокой отдачи · архитектурная схема · смета первого пилота · отдача в ₽/час владельца',
    quote: '«Говорю: занеси — он это всё куда-то разносит, таблички создаёт, но я сам в них не захожу.»',
    quoteWho: '— собственник, третья неделя внедрения',
    note: 'В рабочее время отвечаем в течение часа, иначе — следующим рабочим утром. Карта ваша, даже если дальше не работаем.',
  },
  en: {
    eyebrow: 'Free AI diagnostics for your business',
    heading: 'We map your business with AI',
    lede: 'Anton and AI look, in real time, at where your operations are stuck and which bottlenecks AI closes first. You leave with an AI maturity map of your business and a growth plan — whether or not we end up working together.',
    flowKicker: 'How the free diagnostics works',
    steps: [
      { label: 'Request', time: '≈ 1 minute', body: 'Fill in the form. Within an hour we reply via the contact you gave — with confirmation and questions.' },
      { label: 'Brief', time: '5 minutes', body: '3–5 questions about your business, bottlenecks and budget range, so the call goes straight to substance.' },
      { label: 'Call with AI', time: '30–90 minutes', body: 'Anton and AI take your business apart in real time: you watch AI find the levers in your operations.' },
      { label: 'AI maturity map', time: '24–48 hours', body: 'A 5–7 page PDF that reads like a spec for your team.' },
    ],
    mapLabel: 'Inside the map',
    map: 'operations map · 3–5 high-return points · architecture diagram · first pilot estimate · return in $/hour of owner time',
    quote: '“I say: log it — it sorts it all somewhere, creates the tables, but I don’t go into them.”',
    quoteWho: '— a business owner, week three of the implementation',
    note: 'During working hours we reply within an hour, otherwise the next working morning. The map is yours even if we don’t continue.',
  },
}

/**
 * Финальный CTA с формой ИИ-диагностики.
 *
 * Слева — обещание и четыре шага диагностики, справа — форма. Ценника и
 * риск-реверса здесь нет (мандат Тони 2026-08-03): цифры стоят в блоке
 * продукта, снятие страхов — в FAQ. К форме человек приходит решившим.
 *
 * Шаги переехали сюда из отдельной секции DiagnosticFlow (редизайн
 * 2026-09-20). Они стоят в левой колонке, а не лентой над формой во всю
 * ширину: лента отодвигала поля за первый экран блока.
 *
 * Порядок в разметке — заголовок → форма → шаги (аудит 2026-09-21). На
 * телефоне между заголовком и первым полем стояло ~1,7 экрана текста
 * (абзац, четыре шага, состав карты), и кнопки Hero/«Продукта», ведущие
 * на #final-cta, приземляли человека на текст, а не на поля. Теперь
 * колонка на телефоне читается сверху вниз в порядке разметки, а на
 * десктопе сетка ставит шаги под заголовок явными строками
 * (grid-rows + row-start): DOM, порядок табуляции и картинка совпадают
 * на любой ширине — без дублирования разметки и без CSS order.
 */
export function FinalCta() {
  const c = COPY[useLocale()]
  return (
    <section id="final-cta" className="section">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 lg:grid-rows-[auto_1fr] gap-x-12 gap-y-10 lg:gap-y-12 items-start">
        <div className="lg:col-span-6 xl:col-span-5 lg:row-start-1">
          <div className="font-mono text-small uppercase tracking-wider text-muted mb-4">{c.eyebrow}</div>
          <h2 className="text-h2 text-ink text-balance">{c.heading}</h2>
          <p className="mt-6 text-body text-muted">{c.lede}</p>
        </div>

        {/* id="lead-form" — якорь для мобильной sticky-CTA: она ведёт сразу на
            карточку формы, минуя заголовок блока (#final-cta).
            scroll-mt-20 — отступ под sticky-nav при нативном hash-jump. */}
        <div className="lg:col-span-6 xl:col-start-7 lg:row-start-1 lg:row-span-2 scroll-mt-20" id="lead-form">
          {/* Микро-соцдоказательство над формой (пакет обогащения, блок 6) */}
          <blockquote className="mb-6">
            <p className="font-serif italic text-h3 text-ink leading-snug">{c.quote}</p>
            <footer className="mt-2 text-small text-muted">{c.quoteWho}</footer>
          </blockquote>
          <LeadForm />
          <p className="mt-4 text-small text-muted leading-snug">{c.note}</p>
        </div>

        <div className="lg:col-span-6 xl:col-span-5 lg:row-start-2">
          <div className="font-mono text-small uppercase tracking-wider text-muted">{c.flowKicker}</div>
          <ol className="diag-flow mt-6">
            {c.steps.map((s, i) => (
              <li key={s.label} className="diag-flow__step">
                <span className="diag-flow__dot" aria-hidden />
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <h3 className="text-body text-ink font-medium">
                    <span className="font-mono text-small text-muted mr-3">0{i + 1}</span>
                    {s.label}
                  </h3>
                  <span className="font-mono text-small text-muted tabular-nums">{s.time}</span>
                </div>
                <p className="mt-1 text-small text-muted leading-snug">{s.body}</p>
              </li>
            ))}
          </ol>
          <p className="mt-6 text-small text-muted leading-snug">
            <span className="font-mono uppercase tracking-wider text-ink">{c.mapLabel}:</span> {c.map}
          </p>
        </div>
      </div>
    </section>
  )
}
